// SKILLSMITH-OPPORTUNITIES (founder 2026-10-30): Billing › Helper quotes. The platform quotes a
// new helper (a one-off and/or a monthly price); the company's owner or admin Approves (the
// one-off is invoiced, "Helper: <name>" joins the subscription, the build starts) or Declines.
// Hidden while the company has no quotes.
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  InfoTip,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { formatMoney } from "../format";
import { problemText } from "../ops-parts";

type Quotes = OperationOutput<"billing.helperOffers.list">;
type Quote = Quotes["data"][number];

export const helperQuotesKey = (co: string) => ["wallet", co, "helper-offers"] as const;

const STATUS: Record<
  Quote["status"],
  { label: string; tone: "warning" | "info" | "success" | "neutral" }
> = {
  open: { label: "Waiting for you", tone: "warning" },
  building: { label: "Building", tone: "info" },
  added: { label: "Added", tone: "success" },
  declined: { label: "Declined", tone: "neutral" },
  canceled: { label: "Canceled", tone: "neutral" },
};

const money = (q: Quote, minor: number) => formatMoney(minor, q.currency);

export function HelperQuotes({ api, companyId }: { api: Api; companyId: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: helperQuotesKey(companyId),
    queryFn: () => api.client.billing.helperOffers.list({ params: { co: companyId } }),
  });
  const [confirm, setConfirm] = useState<{ quote: Quote; decision: "approve" | "decline" } | null>(
    null,
  );
  const decide = useMutation({
    mutationFn: (v: { quote: Quote; decision: "approve" | "decline" }) =>
      api.client.billing.helperOffers.decide({
        params: { co: companyId, offer_id: v.quote.id },
        body: { decision: v.decision },
      }),
    onSuccess: (q) =>
      toast.add({
        title:
          q.status === "building" ? `Approved: ${q.helper_name}` : `Declined: ${q.helper_name}`,
        ...(q.status === "building" ? { type: "success" } : {}),
      }),
    onError: (e) => toast.add({ title: problemText(e, "Not decided"), type: "error" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["wallet", companyId] }),
  });
  const rows = list.data?.data ?? [];
  if (rows.length === 0) return null;
  const canDecide = list.data?.can_decide ?? false;
  return (
    <section
      aria-labelledby="helper-quotes"
      data-slot="helper-quotes"
      className="flex min-w-0 flex-col gap-2"
    >
      <div className="flex items-center gap-1">
        <h2 id="helper-quotes" className="text-sm font-semibold">
          Helper quotes
        </h2>
        <InfoTip label="About helper quotes">
          A new helper for one of your DEs. Approve: the one-off is invoiced and the monthly price
          joins your subscription.
        </InfoTip>
      </div>
      <ul className="flex flex-col divide-y divide-subtle rounded-lg border border-border bg-card">
        {rows.map((q) => {
          const s = STATUS[q.status];
          return (
            <li
              key={q.id}
              data-quote={q.id}
              data-status={q.status}
              className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{q.helper_name}</span>
                  <Badge variant={s.tone} data-slot="quote-status">
                    {q.status === "open" && !canDecide ? "Waiting" : s.label}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  {q.de ? (
                    <span className="inline-flex items-center gap-1">
                      <Badge variant="outline">DE</Badge>
                      {q.de.name}
                    </span>
                  ) : null}
                  {q.description ? <span className="truncate">{q.description}</span> : null}
                </div>
                {q.invoice || q.subscription_line ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                    {q.invoice ? (
                      <span data-slot="quote-invoice" className="inline-flex items-center gap-1">
                        Invoice {q.invoice.number} · {money(q, q.invoice.amount_minor)}
                        {q.charge_status === "recorded" ? (
                          <>
                            <span className="text-muted-foreground">· Charge pending</span>
                            <InfoTip label="About the pending charge">
                              The charge is taken when billing one-time charges are switched on.
                            </InfoTip>
                          </>
                        ) : null}
                      </span>
                    ) : null}
                    {q.subscription_line && (q.status === "building" || q.status === "added") ? (
                      <span data-slot="quote-line" className="text-muted-foreground">
                        {q.subscription_line.label} {money(q, q.subscription_line.monthly_minor)}
                        /mo
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <dl className="flex shrink-0 gap-4 text-sm tabular-nums">
                <div className="flex flex-col items-end">
                  <dt className="text-xs text-muted-foreground">One-off</dt>
                  <dd>{money(q, q.one_off_minor)}</dd>
                </div>
                <div className="flex flex-col items-end">
                  <dt className="text-xs text-muted-foreground">Monthly</dt>
                  <dd>{money(q, q.monthly_minor)}</dd>
                </div>
              </dl>
              {q.status === "open" && canDecide ? (
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={decide.isPending}
                    onClick={() => setConfirm({ quote: q, decision: "decline" })}
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() => setConfirm({ quote: q, decision: "approve" })}
                  >
                    <CheckIcon aria-hidden="true" />
                    Approve
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm ? confirmTitle(confirm.quote, confirm.decision) : ""}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={confirm?.decision === "decline" ? "destructive" : "default"}
              onClick={() => {
                if (confirm) decide.mutate(confirm);
                setConfirm(null);
              }}
            >
              {confirm?.decision === "decline" ? "Decline" : "Approve"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/** "Approve Interview scheduler: $500.00 now and $99.00/month?" */
function confirmTitle(q: Quote, decision: "approve" | "decline"): string {
  if (decision === "decline") return `Decline ${q.helper_name}?`;
  const parts = [
    q.one_off_minor > 0 ? `${money(q, q.one_off_minor)} now` : null,
    q.monthly_minor > 0 ? `${money(q, q.monthly_minor)}/month` : null,
  ].filter(Boolean);
  return `Approve ${q.helper_name}: ${parts.join(" and ")}?`;
}
